import React, { useState, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stepper, Step,
  StepLabel, Alert, LinearProgress, Chip
} from '@mui/material';
import UsbIcon          from '@mui/icons-material/Usb';
import FlashOnIcon      from '@mui/icons-material/FlashOn';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import MemoryIcon       from '@mui/icons-material/Memory';

const API = 'https://wifi-api.redia-maroc.com/v1';

const PARTS = [
  { file: 'bootloader.bin', offset: 0x1000,  label: 'Bootloader' },
  { file: 'partitions.bin', offset: 0x8000,  label: 'Table partitions' },
  { file: 'boot_app0.bin',  offset: 0xe000,  label: 'Boot app' },
  { file: 'firmware.bin',   offset: 0x10000, label: 'Firmware' },
];

export default function Flash() {
  const [step,     setStep]     = useState(0); // 0=idle 1=connecting 2=flashing 3=done
  const [log,      setLog]      = useState([]);
  const [progress, setProgress] = useState(0);
  const [error,    setError]    = useState('');
  const portRef = useRef(null);

  const addLog = (msg, type = 'info') => setLog(l => [...l, { msg, type }]);

  const isSupported = 'serial' in navigator;

  const handleFlash = async () => {
    setError(''); setLog([]); setProgress(0); setStep(1);

    try {
      // 1. Connexion port série
      addLog('Demande acces port serie...');
      const port = await navigator.serial.requestPort();
      portRef.current = port;
      addLog('Port serie selectionne', 'success');

      // 2. Import dynamique esptool-js
      const { ESPLoader, Transport } = await import('esptool-js');
      setStep(2);

      // 3. Download des binaires
      addLog('Telechargement des binaires depuis le serveur...');
      const fileArray = [];
      for (const part of PARTS) {
        addLog(`  ${part.label}...`);
        const res = await fetch(`${API}/firmware/universal/${part.file}`);
        if (!res.ok) throw new Error(`Impossible de telecharger ${part.file}`);
        const buf = await res.arrayBuffer();
        const data = new Uint8Array(buf);
        fileArray.push({ data, address: part.offset });
        addLog(`  ${part.label} OK (${(buf.byteLength / 1024).toFixed(0)} KB)`, 'success');
      }
      setProgress(20);

      // 4. Connexion ESP32
      addLog('Connexion ESP32 (mise en mode flash)...');
      const transport = new Transport(port, true);
      const loader = new ESPLoader({
        transport,
        baudrate: 115200,
        terminal: {
          clean:     () => {},
          writeLine: (s) => addLog(s),
          write:     (s) => addLog(s),
        },
      });

      await loader.main();
      addLog(`Puce detectee : ${loader.chip.CHIP_NAME}`, 'success');
      setProgress(40);

      // 5. Flash
      addLog('Flash en cours — ne pas debrancher...');
      await loader.writeFlash({
        fileArray,
        flashSize: 'keep',
        flashMode: 'keep',
        flashFreq: 'keep',
        eraseAll:  false,
        compress:  true,
        reportProgress: (fileIndex, written, total) => {
          const pct = 40 + Math.round((fileIndex * 100 + written / total * 100) / PARTS.length * 0.6);
          setProgress(Math.min(pct, 99));
        },
      });

      setProgress(100);
      addLog('Flash termine avec succes !', 'success');
      addLog('Le capteur va redemarrer et apparaitre dans "Non affectes".', 'success');

      // 6. Reset
      await loader.after('hard_reset');
      await transport.disconnect();
      setStep(3);

    } catch (e) {
      setError(e.message || String(e));
      setStep(0);
      addLog('Erreur : ' + (e.message || String(e)), 'error');
      if (portRef.current) {
        try { await portRef.current.close(); } catch {}
      }
    }
  };

  const reset = () => { setStep(0); setLog([]); setProgress(0); setError(''); };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#1F4E79">
          Flash initial capteur
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Installe le firmware universel REDIA sur un ESP32 vierge via USB
        </Typography>
      </Box>

      {!isSupported && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Web Serial API non supportee. Utilise <strong>Chrome</strong> ou <strong>Edge</strong> (pas Firefox).
        </Alert>
      )}

      <Stepper activeStep={step > 0 ? step - 1 : -1} sx={{ mb: 4 }}>
        {['Connexion', 'Telechargement', 'Flash', 'Termine'].map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Panneau principal */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', flex: '1 1 340px' }}>
          <CardContent sx={{ p: 3 }}>
            {step === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Box sx={{ p: 2.5, borderRadius: '50%', bgcolor: '#E3F2FD', display: 'inline-flex', mb: 2 }}>
                  <MemoryIcon sx={{ fontSize: 48, color: '#1F4E79' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} mb={1}>Pret a flasher</Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Branche le capteur ESP32 vierge en USB, maintiens le bouton BOOT enfonce, puis clique Flasher.
                </Typography>
                <Button
                  variant="contained" size="large" startIcon={<UsbIcon />}
                  onClick={handleFlash} disabled={!isSupported}
                  sx={{ bgcolor: '#1F4E79', px: 4, py: 1.5, '&:hover': { bgcolor: '#163a5f' } }}>
                  Connecter et Flasher
                </Button>
              </Box>
            )}

            {(step === 1 || step === 2) && (
              <Box>
                <Typography fontWeight={600} mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlashOnIcon sx={{ color: '#FF9800' }} />
                  Flash en cours...
                </Typography>
                <LinearProgress variant="determinate" value={progress}
                  sx={{ height: 10, borderRadius: 5, mb: 1,
                    '& .MuiLinearProgress-bar': { bgcolor: '#1F4E79' } }} />
                <Typography variant="body2" color="text.secondary" mb={1}>{progress}%</Typography>
              </Box>
            )}

            {step === 3 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 56, color: '#4CAF50', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} color="#4CAF50" mb={1}>
                  Flash reussi !
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Le capteur redemarre. Configure le WiFi via son AP <strong>REDIA-CALIB-XXXX</strong>, puis il apparaitra dans Supervision &rarr; "Non affectes".
                </Typography>
                <Button variant="outlined" onClick={reset}>Flasher un autre capteur</Button>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>{error}</Alert>
            )}
          </CardContent>
        </Card>

        {/* Log terminal */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', flex: '1 1 340px' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography fontWeight={600} mb={1.5}>Journal</Typography>
            <Box sx={{
              bgcolor: '#0D1117', borderRadius: 2, p: 2, minHeight: 200, maxHeight: 360,
              overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.78rem',
            }}>
              {log.length === 0
                ? <Typography sx={{ color: '#555', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    En attente...
                  </Typography>
                : log.map((l, i) => (
                  <Box key={i} sx={{
                    color: l.type === 'success' ? '#4CAF50' : l.type === 'error' ? '#F44336' : '#90CAF9',
                    mb: 0.3,
                  }}>
                    {l.msg}
                  </Box>
                ))
              }
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Infos */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 3, bgcolor: '#F3F8FF' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography fontWeight={600} mb={1.5} color="#1F4E79">Firmware embarque</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PARTS.map(p => (
              <Chip key={p.file} label={`${p.label} @ 0x${p.offset.toString(16).toUpperCase()}`}
                size="small" sx={{ fontFamily: 'monospace', bgcolor: '#E3F2FD', color: '#1F4E79' }} />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Chrome / Edge requis (Web Serial API). Le capteur sera configure avec un ID base sur sa MAC.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
